import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import JsonLd from "../../components/seo/json-ld";
import InsightCard from "../../components/insights/insight-card";
import {
  INSIGHTS_SITE_URL,
  getPublicInsights,
  listPublishedInsights,
} from "../../services/insights-admin-service";
import { joinKeywords } from "../../components/insights/insight-utils";
import "../../assets/css/insights.css";

const emptyValue = "All";

const buildListingSchemas = (posts) => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${INSIGHTS_SITE_URL}/#organization`,
    name: "Avani Cimcon Technologies",
    url: INSIGHTS_SITE_URL,
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${INSIGHTS_SITE_URL}/#website`,
    name: "Avani Cimcon Technologies",
    url: INSIGHTS_SITE_URL,
    publisher: { "@id": `${INSIGHTS_SITE_URL}/#organization` },
  },
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${INSIGHTS_SITE_URL}/resources/insights#blog`,
    name: "Insights & Perspectives",
    url: `${INSIGHTS_SITE_URL}/resources/insights`,
    description:
      "Research-led thinking on enterprise software engineering, AI enablement, legacy modernization and long-term platform evolution.",
    publisher: { "@id": `${INSIGHTS_SITE_URL}/#organization` },
    blogPost: posts.slice(0, 12).map((post) => ({ "@id": post.canonicalUrl })),
  },
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${INSIGHTS_SITE_URL}/resources/insights#webpage`,
    name: "Insights & Perspectives | Avani Cimcon",
    url: `${INSIGHTS_SITE_URL}/resources/insights`,
    isPartOf: { "@id": `${INSIGHTS_SITE_URL}/#website` },
    about: { "@id": `${INSIGHTS_SITE_URL}/#organization` },
    description:
      "Research-led thinking on enterprise software engineering, AI enablement, legacy modernization and long-term platform evolution.",
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: post.canonicalUrl,
      name: post.title,
    })),
  },
];

const InsightsListing = () => {
  const allPublished = useMemo(() => listPublishedInsights(), []);
  const categories = useMemo(() => Array.from(new Set(getPublicInsights().map((post) => post.category))).sort(), []);
  const tags = useMemo(
    () => Array.from(new Set(getPublicInsights().flatMap((post) => post.tags || []))).sort((a, b) => a.localeCompare(b)),
    []
  );

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(emptyValue);
  const [tag, setTag] = useState(emptyValue);

  const topFeatured = allPublished
    .filter((post) => post.featured)
    .sort((a, b) => {
      const rankA = Number(a.featuredRank || 999);
      const rankB = Number(b.featuredRank || 999);
      if (rankA !== rankB) return rankA - rankB;
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    })
    .slice(0, 3);

  const featured = topFeatured.length >= 3 ? topFeatured : allPublished.slice(0, 3);

  const filtered = allPublished.filter((post) => {
    const query = search.trim().toLowerCase();
    const matchesQuery =
      !query ||
      [post.title, post.subtitle, post.excerpt, post.category, ...(post.tags || [])]
        .join(" ")
        .toLowerCase()
        .includes(query);
    const matchesCategory = category === emptyValue || post.category === category;
    const matchesTag = tag === emptyValue || (post.tags || []).includes(tag);
    return matchesQuery && matchesCategory && matchesTag;
  });

  const pageTitle = "Insights & Perspectives | Avani Cimcon";
  const pageDescription =
    "Research-led thinking on enterprise software engineering, AI enablement, legacy modernization and long-term platform evolution.";

  return (
    <React.Fragment>
      <Helmet defer={false}>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={joinKeywords(tags)} />
        <link rel="canonical" href={`${INSIGHTS_SITE_URL}/resources/insights`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${INSIGHTS_SITE_URL}/resources/insights`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`${INSIGHTS_SITE_URL}/images/og-homepage.jpg`} />
        <meta property="og:site_name" content="Avani Cimcon Technologies" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${INSIGHTS_SITE_URL}/images/og-homepage.jpg`} />
      </Helmet>
      <JsonLd data={buildListingSchemas(allPublished)} />

      <main className="insights-page">
        <section className="insights-hero">
          <div className="container insights-hero-inner">
            <p className="insights-kicker">Resources</p>
            <h1>Insights &amp; Perspectives</h1>
            <p>
              Research-led thinking on enterprise software engineering, AI enablement, legacy modernization and long-term platform evolution.
            </p>

            <div className="insights-panel">
              <div className="insights-meta-row">
                <span className="insights-count">{allPublished.length} published insights</span>
                <span>{featured.length} featured on homepage</span>
                <span>Canonical listing: /resources/insights</span>
              </div>

              <div className="insights-toolbar" aria-label="Insight filters">
                <input
                  className="form-control"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title, excerpt or tag"
                />
                <select className="custom-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value={emptyValue}>All categories</option>
                  {categories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select className="custom-select" value={tag} onChange={(e) => setTag(e.target.value)}>
                  <option value={emptyValue}>All tags</option>
                  {tags.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <div className="d-flex align-items-center">
                  <Link to="/contact-us" className="hero-v2-btn-primary w-100 justify-content-center">
                    Discuss Your Modernization Roadmap
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="insights-section">
          <div className="container">
            <div className="insights-section-header">
              <div>
                <p className="insights-kicker" style={{ color: "var(--act-orange)" }}>
                  Featured
                </p>
                <h2>Top 3 insights</h2>
              </div>
              <p>
                The homepage preview surfaces the highest-priority thought leadership first, falling back to the latest published insight if fewer featured posts are available.
              </p>
            </div>

            <div className="row">
              {featured.map((post) => (
                <div key={post.slug} className="col-lg-4 col-md-6 mb-4">
                  <InsightCard post={post} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="insights-section pt-0">
          <div className="container">
            <div className="insights-section-header">
              <div>
                <p className="insights-kicker" style={{ color: "var(--act-orange)" }}>
                  All insights
                </p>
                <h2>Research-led articles for enterprise teams</h2>
              </div>
              <p>
                Showing newest first. Search by title, excerpt or tag, then filter by category or theme to narrow the list.
              </p>
            </div>

            <div className="row insights-grid">
              {filtered.length ? (
                filtered.map((post) => (
                  <div key={post.slug} className="col-lg-4 col-md-6 mb-4">
                    <InsightCard post={post} />
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <div className="insights-empty">
                    No insights matched your search. Try a different tag, category or keyword.
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="insights-section pt-0">
          <div className="container">
            <section className="insight-cta-band">
              <div>
                <p className="insight-cta-eyebrow" style={{ color: "rgba(255,255,255,.72)" }}>
                  Next step
                </p>
                <h2>Discuss Your Modernization Roadmap</h2>
                <p>
                  Explore a practical modernization path for legacy systems, AI enablement and long-lived enterprise platforms.
                </p>
              </div>
              <Link to="/contact-us" className="hero-v2-btn-primary">
                Talk to Us
              </Link>
            </section>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
};

export default InsightsListing;
